/**
 * One-time sweep: any expense row whose `Receipt File` is a data: URL gets
 * hoisted into the Storage bucket. Runs automatically at sign-in (wired in
 * main.ts) and can be re-triggered from Configs → Data Management.
 *
 * Idempotent — only acts on rows whose value still starts with "data:".
 */
import { DB } from "./db";
import { CLOUD_USER } from "./supabase";
import { toast } from "./dom";
import { uploadReceiptDataUrl } from "./storage";
import { State } from "../state";
import { render } from "../state";

let _running = false;

export interface SweepResult { migrated: number; skipped: number; failed: number; }

export async function migrateReceiptsToStorage(
  progressCb?: (p: { current: number; total: number }) => void,
): Promise<SweepResult> {
  if (_running) return { migrated: 0, skipped: 0, failed: 0 };
  if (!CLOUD_USER)   return { migrated: 0, skipped: 0, failed: 0 };
  _running = true;
  const exp = DB.table("expenses") || [];
  const todo: number[] = [];
  for (let i = 0; i < exp.length; i++) {
    const v = exp[i] && exp[i]["Receipt File"];
    if (v && typeof v === "string" && v.startsWith("data:")) todo.push(i);
  }
  if (!todo.length) { _running = false; return { migrated: 0, skipped: exp.length, failed: 0 }; }
  let migrated = 0, failed = 0;
  for (let k = 0; k < todo.length; k++) {
    const i = todo[k];
    const row = exp[i];
    progressCb?.({ current: k + 1, total: todo.length });
    try {
      const path = await uploadReceiptDataUrl(
        row["Receipt File"],
        row["Receipt File Name"] || `receipt-${k + 1}`,
        row["Receipt File Type"] || "application/octet-stream",
      );
      const merged = { ...row, "Receipt File": path };
      if (row._uuid) {
        await DB.updateByUuid("expenses", row._uuid, merged);
      } else {
        // Shouldn't happen on a freshly-loaded session; keep local cache consistent and warn.
        row["Receipt File"] = path;
        console.warn("[receipt-migration] row had no _uuid — local cache updated, cloud row not touched");
      }
      migrated++;
    } catch (e) {
      console.error("[receipt-migration] row", i, e);
      failed++;
    }
  }
  DB.save();
  _running = false;
  if (migrated) {
    toast(`Migrated ${migrated} receipt${migrated === 1 ? "" : "s"} to Storage${failed ? ` (${failed} failed)` : ""}`);
    if (State.section === "expenses") render();
  }
  return { migrated, skipped: exp.length - todo.length, failed };
}
